/**
 * NotifyMembersDialog — iter-92
 *
 * After an admin edits event fields (title/time/location/details/programme)
 * the backend records the diff on `event.pending_changes`. This dialog
 * lets the admin (a) preview that diff, (b) pick an audience, (c) write
 * an optional message, and (d) fire ONE consolidated "Event Updated"
 * email to everyone in the chosen audience.
 *
 * Confirmed deps are automatically included by default — they've committed
 * to play so they need to know the same as members.
 */
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../App";

const AUDIENCE_OPTIONS = [
  { value: "attending_maybe", label: "Attending + Maybe", hint: "Default — anyone planning to play" },
  { value: "attending", label: "Attending only", hint: "Just confirmed Yes responses" },
  { value: "all", label: "All members", hint: "Everyone, including Pending and No" },
  { value: "non_responders", label: "Non-responders only", hint: "Members who haven't replied yet" },
];

const ChangeRow = ({ field, entry }) => {
  if (field === "programme") {
    const added = entry?.added || [];
    const removed = entry?.removed || [];
    return (
      <div className="py-2 border-b border-border/40 last:border-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{entry?.label || "Programme"}</div>
        {added.length > 0 && (
          <div className="text-sm text-green-400">
            {added.map((x, i) => <div key={`a${i}`}>+ {x}</div>)}
          </div>
        )}
        {removed.length > 0 && (
          <div className="text-sm text-red-400">
            {removed.map((x, i) => <div key={`r${i}`}>− {x}</div>)}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="py-2 border-b border-border/40 last:border-0">
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{entry?.label || field}</div>
      <div className="text-sm flex flex-wrap items-center gap-2">
        <span className="text-red-400 line-through">{entry?.from || "—"}</span>
        <span className="text-muted-foreground">→</span>
        <span className="text-green-400 font-semibold">{entry?.to || "—"}</span>
      </div>
    </div>
  );
};

export default function NotifyMembersDialog({ open, onOpenChange, eventId, pendingChanges, onSent }) {
  const [audience, setAudience] = useState("attending_maybe");
  const [includeDeps, setIncludeDeps] = useState(true);
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);

  const changeEntries = useMemo(
    () => Object.entries(pendingChanges || {}),
    [pendingChanges]
  );
  const hasChanges = changeEntries.length > 0;

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await apiClient.post(`/events/${eventId}/notify-update`, {
        audience,
        include_deps: includeDeps,
        custom_message: customMessage.trim() || null,
      });
      const sent = res.data?.sent ?? 0;
      const total = res.data?.total_recipients ?? 0;
      if (sent > 0) {
        toast.success(`Update sent to ${sent} of ${total} recipient${total === 1 ? "" : "s"}`);
      } else if (total === 0) {
        toast.info(res.data?.message || "No matching recipients for this audience");
      } else {
        toast.error("Update email failed to send");
      }
      onSent?.();
      onOpenChange(false);
      setCustomMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send update");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="notify-members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Notify members of updates
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Change summary */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              What&rsquo;s changed since last notification
            </Label>
            <div className="mt-2 rounded-md border border-border/60 bg-muted/30 p-3" data-testid="notify-changes-list">
              {hasChanges ? (
                changeEntries.map(([field, entry]) => (
                  <ChangeRow key={field} field={field} entry={entry} />
                ))
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No tracked changes since last notification. You can still send
                  a custom message below if you want.
                </p>
              )}
            </div>
          </div>

          {/* Audience */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Audience</Label>
            <RadioGroup value={audience} onValueChange={setAudience} className="mt-2 space-y-2">
              {AUDIENCE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`audience-${opt.value}`}
                  className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:bg-muted/40"
                  data-testid={`audience-option-${opt.value}`}
                >
                  <RadioGroupItem value={opt.value} id={`audience-${opt.value}`} className="mt-1" />
                  <div>
                    <div className="font-medium text-sm">{opt.label}</div>
                    <div className="text-xs text-muted-foreground">{opt.hint}</div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Include deps */}
          <label className="flex items-start gap-3 rounded-md border border-border/60 p-3 cursor-pointer hover:bg-muted/40">
            <Checkbox
              checked={includeDeps}
              onCheckedChange={(v) => setIncludeDeps(!!v)}
              data-testid="include-deps-checkbox"
            />
            <div>
              <div className="font-medium text-sm">Include confirmed deps</div>
              <div className="text-xs text-muted-foreground">
                Deps who&rsquo;ve already accepted their invitation will also receive the update.
              </div>
            </div>
          </label>

          {/* Custom message */}
          <div>
            <Label htmlFor="notify-custom-msg" className="text-xs uppercase tracking-wide text-muted-foreground">
              Custom message <span className="text-muted-foreground/60 lowercase">(optional)</span>
            </Label>
            <Textarea
              id="notify-custom-msg"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="e.g. We've moved the rehearsal to the church hall as the school is closed."
              rows={3}
              className="mt-2"
              data-testid="notify-custom-message"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="notify-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending || (!hasChanges && !customMessage.trim())} data-testid="notify-send-btn">
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Mail className="w-4 h-4 mr-2" /> Send update email</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
